import './MainScreen.css';
import GButton from '../../components/ui/base/GButton';
import { Sections } from './Sections';
import { BigWinns } from './BigWinns';
import { GameWithCategories } from './GameWithCategories';
import useMainStore from '../../store/mainState';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CasinoIcon } from '../../assets/icons/casino.svg';
import { ReactComponent as SportIcon } from '../../assets/icons/sport.svg';
import { RafflePreview } from './RafflePreview';
import { LiveBets } from './LiveBets';
import { useTranslation } from 'react-i18next';
import WorldCupPopup from '../../components/ui/popup/WorldCupPopup';

const WORLD_CUP_POPUP_KEY = 'worldCup2026PopupShown';

const MainScreen = () => {
  const { user, setIsSignUpOpen, setMainContentWidth, setActiveItem, setActiveSubItem } = useMainStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const mainScreen = useRef<HTMLDivElement>(null);
  const [isWorldCupOpen, setIsWorldCupOpen] = useState(false);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;

      setMainContentWidth(width);
    });

    if (mainScreen.current) {
      observer.observe(mainScreen.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem(WORLD_CUP_POPUP_KEY)) return;
    const t = setTimeout(() => {
      setIsWorldCupOpen(true);
      sessionStorage.setItem(WORLD_CUP_POPUP_KEY, '1');
    }, 900);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="main-screen" ref={mainScreen}>
      <section className='flex flex-row rounded-[6px] md:rounded-[25px] overflow-hidden relative p-[10px] md:p-[0px]'>
        {/* World Cup 2026 stadium background */}
        <div className='absolute inset-0 overflow-hidden pointer-events-none'>
          {/* Base sky-to-pitch gradient */}
          <div className='absolute inset-0 bg-gradient-to-b from-[#020617] via-[#0a1d3c] via-60% to-[#064e2e]' />

          {/* Distant city/horizon line */}
          <div className='absolute left-0 right-0 top-[55%] h-[2px] bg-gradient-to-r from-transparent via-[#4DBDFF]/30 to-transparent' />

          {/* Stadium spotlights */}
          <div className='absolute top-[-20%] left-1/2 -translate-x-1/2 w-[80%] h-[80%] bg-[#FFD700]/12 rounded-full blur-[100px]' style={{ animation: 'wcCardGlow 4s ease-in-out infinite' }} />
          <div className='absolute top-[-10%] left-[10%] w-[30%] h-[60%] bg-[#4DBDFF]/15 rounded-full blur-[80px]' />
          <div className='absolute top-[-10%] right-[10%] w-[30%] h-[60%] bg-[#4DBDFF]/15 rounded-full blur-[80px]' />

          {/* Diagonal light beams */}
          <div className='absolute top-0 left-[20%] w-[100px] h-[60%] bg-white/[0.04] blur-[40px] rotate-[18deg] origin-top' />
          <div className='absolute top-0 right-[20%] w-[100px] h-[60%] bg-white/[0.04] blur-[40px] -rotate-[18deg] origin-top' />

          {/* Watermark giant 2026 */}
          <div className='absolute inset-0 flex items-center justify-center pointer-events-none select-none'>
            <span className='font-black italic text-transparent bg-clip-text bg-gradient-to-b from-white/[0.06] to-white/[0.02] text-[140px] md:text-[220px] lg:text-[300px] xl:text-[380px] leading-none tracking-tighter'>
              2026
            </span>
          </div>

          {/* Pitch grass stripes (bottom half) */}
          <div
            className='absolute bottom-0 left-0 right-0 h-[50%] opacity-25'
            style={{
              backgroundImage:
                'repeating-linear-gradient(90deg, transparent, transparent 24px, rgba(255,255,255,0.05) 24px, rgba(255,255,255,0.05) 48px)',
            }}
          />

          {/* Pitch perspective fade */}
          <div className='absolute bottom-0 left-0 right-0 h-[40%] bg-gradient-to-t from-[#022c1a] via-[#064e2e]/40 to-transparent' />

          {/* Center pitch arc (soft) */}
          <div className='absolute bottom-[-30%] left-1/2 -translate-x-1/2 w-[120%] h-[60%] rounded-[50%] border border-white/[0.08] pointer-events-none' />

          {/* Floating decorative soccer balls (off-edge, subtle) */}
          <div
            className='absolute top-[20%] left-[3%] text-[28px] md:text-[40px] opacity-30 hidden md:block'
            style={{ animation: 'wcCardSpin 8s linear infinite' }}
          >
            ⚽
          </div>
          <div
            className='absolute top-[15%] right-[5%] text-[22px] md:text-[34px] opacity-25 hidden md:block'
            style={{ animation: 'wcCardSpin 10s linear infinite reverse' }}
          >
            ⚽
          </div>

          {/* Floating trophy (left text area background, faint) */}
          <div
            className='absolute bottom-[18%] left-[8%] text-[34px] md:text-[50px] opacity-20 hidden md:block'
            style={{ animation: 'wcCardFloat 4s ease-in-out infinite' }}
          >
            🏆
          </div>

          {/* Confetti specks */}
          {[
            { c: '#FFD700', t: 12, l: 18, s: 5 },
            { c: '#4DBDFF', t: 28, l: 75, s: 6 },
            { c: '#FF4D6D', t: 8, l: 55, s: 4 },
            { c: '#10B981', t: 40, l: 92, s: 5 },
            { c: '#FFFFFF', t: 22, l: 38, s: 3 },
            { c: '#FFD700', t: 50, l: 8, s: 4 },
            { c: '#4DBDFF', t: 65, l: 88, s: 4 },
            { c: '#FFFFFF', t: 72, l: 25, s: 3 },
          ].map((p, i) => (
            <span
              key={i}
              className='absolute rounded-sm'
              style={{
                top: `${p.t}%`,
                left: `${p.l}%`,
                width: `${p.s}px`,
                height: `${p.s * 0.5}px`,
                background: p.c,
                opacity: 0.7,
                animation: `wcCardFloat ${3 + (i % 4)}s ease-in-out infinite`,
                animationDelay: `${i * 0.3}s`,
              }}
            />
          ))}

          {/* Vignette */}
          <div className='absolute inset-0 bg-radial-gradient pointer-events-none' style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.35) 100%)' }} />
        </div>

        {/* Left side - Text content (hidden on mobile) */}
        <div className='hidden md:flex flex-1 md:py-[20px] pl-[18px] lg:my-[24px] lg:pl-[55px] xl:my-[34px] xl:pl-[75px] flex-col z-[1] justify-center'>
          <h1 className='md:text-[20px] lg:text-[30px] xl:text-[45px] font-bold text-white italic'>{t('mainScreen.stayUntamed')}</h1>
          <h2 className='md:text-[10px] lg:text-[16px] xl:text-[22px] font-bold text-white/80 mt-[2px] md:mt-[5px] italic'>{t('mainScreen.signUpGet')}</h2>
          <h1 className='whitespace-nowrap md:text-[20px] lg:text-[30px] xl:text-[45px] font-bold text-white flex items-center italic'>{t('mainScreen.upTo')} &nbsp; <span className='font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0086D3] via-[#4DBDFF] to-[#4DBDFF]'> $20,000.00</span></h1>
          <h3 className='md:text-[10px] lg:text-[16px] xl:text-[22px] font-bold text-white/80 italic'>{t('mainScreen.inCasinoOrSports')}</h3>
          {!user && <GButton children={t('common.joinNow')} textStyle="font-bold" className="flex-1 rounded-full xl:h-[50px] xl:min-h-[50px] xl:max-h-[50px] h-[30px] max-h-[30px] mt-[10px] md:mt-[25px] min-h-[30px] max-w-[200px]" onClick={() => { setIsSignUpOpen(true) }} />}
        </div>

        {/* Casino & Sports cards - full width on mobile */}
        <div className='flex-1 flex flex-row gap-[8px] md:gap-[16px] items-center justify-center z-[1] py-[10px] px-[6px] md:pr-[20px]'>
          {/* Casino Card */}
          <button
            onClick={() => { setActiveItem('casino'); setActiveSubItem('All'); navigate('/casino'); }}
            className='relative flex-1 h-[140px] md:h-[160px] lg:h-[200px] xl:h-[240px] rounded-[12px] md:rounded-[16px] overflow-hidden group cursor-pointer transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_25px_rgba(0,134,211,0.5)]'
          >
            <img src={require("../../assets/images/casino-banner.png")} alt="Casino" className='absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110' />
            <div className='absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent transition-opacity duration-300 group-hover:from-black/50'></div>
            <div className='absolute inset-0 border-2 border-transparent rounded-[12px] md:rounded-[16px] transition-all duration-300 group-hover:border-[#4DBDFF]/60 group-hover:shadow-[inset_0_0_20px_rgba(77,189,255,0.15)]'></div>
            <div className='absolute bottom-[10px] md:bottom-[12px] left-0 right-0 flex items-center justify-center gap-[6px] transition-all duration-300 group-hover:bottom-[14px] md:group-hover:bottom-[18px]'>
              <CasinoIcon className='w-[16px] h-[16px] md:w-[18px] md:h-[18px] text-white [&_*]:fill-white transition-transform duration-300 group-hover:scale-110' />
              <span className='text-white font-bold text-[16px] md:text-[16px] lg:text-[20px] transition-all duration-300 group-hover:text-[#4DBDFF]'>{t('nav.casino')}</span>
            </div>
          </button>

          {/* World Cup 2026 Card */}
          <button
            onClick={() => { setActiveItem('sport'); setActiveSubItem(''); navigate('/sport'); }}
            className='relative flex-1 h-[140px] md:h-[160px] lg:h-[200px] xl:h-[240px] rounded-[12px] md:rounded-[16px] overflow-hidden group cursor-pointer transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(255,165,0,0.6)]'
            aria-label='World Cup 2026'
          >
            {/* Stadium-night background */}
            <div className='absolute inset-0 bg-gradient-to-br from-[#020617] via-[#0a1d3c] to-[#022c1a]' />

            {/* Gold spotlight glow */}
            <div
              className='absolute -top-[20%] left-1/2 -translate-x-1/2 w-[140%] h-[80%] rounded-full blur-3xl pointer-events-none bg-[#FFD700]/20'
              style={{ animation: 'wcCardGlow 3s ease-in-out infinite' }}
            />

            {/* Pitch stripes at bottom */}
            <div
              className='absolute bottom-0 left-0 right-0 h-[42%] opacity-30 pointer-events-none'
              style={{
                backgroundImage:
                  'repeating-linear-gradient(90deg, transparent, transparent 10px, rgba(255,255,255,0.05) 10px, rgba(255,255,255,0.05) 20px)',
              }}
            />
            <div className='absolute bottom-0 left-0 right-0 h-[35%] bg-gradient-to-t from-[#022c1a] to-transparent pointer-events-none' />

            {/* Border glow on hover */}
            <div className='absolute inset-0 border-2 border-[#FFD700]/20 rounded-[12px] md:rounded-[16px] transition-all duration-300 group-hover:border-[#FFD700]/70 group-hover:shadow-[inset_0_0_30px_rgba(255,215,0,0.18)] pointer-events-none' />

            {/* Diagonal shimmer sweep */}
            <div className='absolute inset-0 overflow-hidden pointer-events-none'>
              <div
                className='absolute top-0 -left-1/3 w-1/3 h-full bg-gradient-to-r from-transparent via-white/25 to-transparent'
                style={{ animation: 'wcCardShimmer 3.5s ease-in-out infinite' }}
              />
            </div>

            {/* Top-right "LIVE" badge */}
            <div className='absolute top-[6px] right-[6px] md:top-[10px] md:right-[10px] z-10 flex items-center gap-[3px] md:gap-[4px] px-[5px] md:px-[7px] py-[2px] md:py-[3px] rounded-full bg-[#FF4D6D]/90 backdrop-blur-sm border border-white/20 shadow-[0_2px_8px_rgba(255,77,109,0.5)]'>
              <span className='w-[4px] h-[4px] md:w-[5px] md:h-[5px] rounded-full bg-white animate-pulse' />
              <span className='text-white font-black text-[8px] md:text-[9px] tracking-wider uppercase'>Live</span>
            </div>

            {/* Content */}
            <div className='relative z-10 h-full flex flex-col items-center justify-center px-[4px] md:px-[8px]'>
              {/* Host flags */}
              <div
                className='flex gap-[2px] md:gap-[4px] text-[10px] md:text-[14px] lg:text-[16px] mb-[2px] md:mb-[4px] leading-none'
                style={{ animation: 'wcCardFloat 3s ease-in-out infinite' }}
              >
                <span>🇺🇸</span>
                <span>🇨🇦</span>
                <span>🇲🇽</span>
              </div>

              {/* WORLD CUP */}
              <div className='text-white font-black italic tracking-tight text-[8px] md:text-[10px] lg:text-[13px] xl:text-[16px] leading-none drop-shadow-[0_2px_8px_rgba(77,189,255,0.4)]'>
                WORLD CUP
              </div>

              {/* 2026 (gold) */}
              <div className='font-black italic leading-[0.95] mt-[1px] text-[26px] md:text-[32px] lg:text-[48px] xl:text-[64px] bg-clip-text text-transparent bg-gradient-to-b from-[#FFE680] via-[#FFA500] to-[#FF6B00] drop-shadow-[0_2px_18px_rgba(255,165,0,0.45)]'>
                2026
              </div>

              {/* Ball + Trophy */}
              <div className='flex items-center gap-[6px] md:gap-[8px] mt-[2px] md:mt-[4px]'>
                <span
                  className='text-[14px] md:text-[18px] lg:text-[22px] xl:text-[26px] inline-block leading-none drop-shadow-[0_2px_6px_rgba(255,255,255,0.4)]'
                  style={{ animation: 'wcCardSpin 4s linear infinite' }}
                >
                  ⚽
                </span>
                <span
                  className='text-[14px] md:text-[18px] lg:text-[22px] xl:text-[26px] inline-block leading-none drop-shadow-[0_2px_6px_rgba(255,215,0,0.6)]'
                  style={{ animation: 'wcCardFloat 3s ease-in-out infinite' }}
                >
                  🏆
                </span>
              </div>

              {/* CTA hint */}
              <div className='mt-[3px] md:mt-[6px] flex items-center gap-[3px] text-[8px] md:text-[10px] lg:text-[12px] font-black uppercase tracking-[0.1em] text-[#FFD700] group-hover:text-white transition-colors duration-300'>
                Bet Now
                <svg width='10' height='10' viewBox='0 0 24 24' fill='none' className='transition-transform duration-300 group-hover:translate-x-[3px]'>
                  <path d='M5 12h14M13 6l6 6-6 6' stroke='currentColor' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
              </div>
            </div>
          </button>

          {/* Sports Card */}
          <button
            onClick={() => { setActiveItem('sport'); setActiveSubItem(''); navigate('/sport'); }}
            className='relative flex-1 h-[140px] md:h-[160px] lg:h-[200px] xl:h-[240px] rounded-[12px] md:rounded-[16px] overflow-hidden group cursor-pointer transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_25px_rgba(0,134,211,0.5)]'
          >
            <img src={require("../../assets/images/sport-banner.png")} alt="Sports" className='absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110' />
            <div className='absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent transition-opacity duration-300 group-hover:from-black/50'></div>
            <div className='absolute inset-0 border-2 border-transparent rounded-[12px] md:rounded-[16px] transition-all duration-300 group-hover:border-[#4DBDFF]/60 group-hover:shadow-[inset_0_0_20px_rgba(77,189,255,0.15)]'></div>
            <div className='absolute bottom-[10px] md:bottom-[12px] left-0 right-0 flex items-center justify-center gap-[6px] transition-all duration-300 group-hover:bottom-[14px] md:group-hover:bottom-[18px]'>
              <SportIcon className='w-[16px] h-[16px] md:w-[18px] md:h-[18px] text-white [&_*]:fill-white transition-transform duration-300 group-hover:scale-110' />
              <span className='text-white font-bold text-[16px] md:text-[16px] lg:text-[20px] transition-all duration-300 group-hover:text-[#4DBDFF]'>{t('nav.sports')}</span>
            </div>
          </button>
        </div>
      </section>
      <BigWinns />
      <Sections />
      <GameWithCategories title={t('mainScreen.slot')} categoryType="SLOT" component="" />
      <GameWithCategories title={t('mainScreen.hotGames')} comment="HOT" />
      <RafflePreview />
      <LiveBets />
      <WorldCupPopup isOpen={isWorldCupOpen} onClose={() => setIsWorldCupOpen(false)} />
    </div>
  );
};

export default MainScreen;